import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CalendarTodayOutlined as CalendarIcon,
  CallOutlined as CallIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  LocationOnOutlined as LocationOnOutlinedIcon,
  MailOutline as MailIcon,
  PersonAddAlt1Outlined as PersonAddIcon,
  PersonOffOutlined as PersonOffIcon,
  Search as SearchIcon,
  VisibilityOutlined as VisibilityIcon,
} from '@mui/icons-material';
import { authAPI, userManagementAPI } from '../../services/api';
import './UserManagement.scss';

const ROWS_PER_PAGE = 10;
const USER_TYPE_OPTIONS = ['Buyer', 'Owner', 'Broker'];
const ROLE_OPTIONS = ['Admin', 'User'];
const getDefaultAddForm = () => ({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
  role: 'User',
  userType: 'Buyer',
});
const getDefaultEditForm = () => ({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  role: 'User',
  userType: '',
});

const normalizeUserTypeOption = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (normalized === 'buyer') return 'Buyer';
  if (normalized === 'owner') return 'Owner';
  if (normalized === 'broker') return 'Broker';
  return '';
};

const getDateLabel = (value) => {
  if (!value) {
    return '--';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '--';
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  });
};

const mapStatus = (user) => {
  if (user?.is_deleted === true || user?.isDeleted === true || user?.deletedAt || user?.deleted_at) {
    return 'Deleted';
  }

  if (typeof user?.is_deleted === 'number' && user.is_deleted === 1) {
    return 'Deleted';
  }

  if (typeof user?.is_active === 'number') {
    return user.is_active === 1 ? 'Active' : 'Inactive';
  }

  if (typeof user?.is_active === 'boolean') {
    return user.is_active ? 'Active' : 'Inactive';
  }

  if (typeof user?.status === 'string') {
    return user.status;
  }

  if (typeof user?.isBlocked === 'boolean') {
    return user.isBlocked ? 'Inactive' : 'Active';
  }

  if (typeof user?.isActive === 'boolean') {
    return user.isActive ? 'Active' : 'Inactive';
  }

  return 'Active';
};

const normalizeStatusLabel = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();

  if (
    normalized === 'deleted'
    || normalized === 'deactivated'
    || normalized === 'delete'
    || normalized === 'removed'
    || normalized === 'disabled'
  ) {
    return 'Deleted';
  }

  if (
    normalized === 'inactive'
    || normalized === 'blocked'
    || normalized === 'in-active'
  ) {
    return 'Inactive';
  }

  if (normalized === 'active') {
    return 'Active';
  }

  return value || 'Active';
};

const isDeletedUser = (user) => {
  const status = String(normalizeStatusLabel(mapStatus(user)) || '')
    .trim()
    .toLowerCase();

  return status === 'deleted' || status === 'deactivated';
};

const isInactiveUser = (user) => normalizeStatusLabel(mapStatus(user)) === 'Inactive';

const getStatusChipClass = (status) => {
  const normalized = String(normalizeStatusLabel(status) || '')
    .trim()
    .toLowerCase();

  if (normalized === 'active') {
    return 'chip-status-active';
  }

  if (normalized === 'inactive' || normalized === 'blocked') {
    return 'chip-status-inactive';
  }

  if (normalized === 'deleted' || normalized === 'deactivated') {
    return 'chip-status-deleted';
  }

  return 'chip-status-default';
};

const getRoleChipClass = (role) => {
  const normalized = String(role || '')
    .trim()
    .toLowerCase();

  return normalized === 'admin' ? 'chip-admin' : 'chip-user';
};

const getUserTypeChipClass = (userType) => {
  const normalized = String(userType || '')
    .trim()
    .toLowerCase();

  if (normalized === 'owner') {
    return 'chip-type-owner';
  }

  if (normalized === 'broker') {
    return 'chip-type-broker';
  }

  if (normalized === 'buyer') {
    return 'chip-type-buyer';
  }

  return 'chip-type-default';
};

const mapRole = (user) => {
  if (user?.role_id === 1) {
    return 'Admin';
  }

  if (user?.role_id === null || user?.roleId === null) {
    return 'User';
  }

  return (
    user?.role
    || user?.roleName
    || user?.role_name
    || user?.roleDetails?.name
    || user?.userRole
    || '--'
  );
};

const mapUserType = (user) => (
  user?.user_type
  || user?.userType
  || user?.type
  || '--'
);

const getNormalizedRoleForFilter = (user) => {
  const roleLabel = String(mapRole(user) || user?.user_type || user?.userType || '')
    .trim()
    .toLowerCase();
  if (roleLabel === 'admin') {
    return 'Admin';
  }
  return 'User';
};

const mapName = (user) => {
  if (user?.name) {
    return user.name;
  }

  const fullName = [
    user?.firstName || user?.first_name,
    user?.lastName || user?.last_name,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();
  if (fullName) {
    return fullName;
  }

  return user?.user_name || '--';
};

const getInitials = (value) => {
  const safeName = String(value || '').trim();
  if (!safeName) {
    return 'U';
  }

  return safeName
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
};

const getUsersFromResponse = (response) => {
  const payload = response?.data?.data ?? response?.data;

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.users)) {
    return payload.users;
  }

  if (Array.isArray(payload?.results)) {
    return payload.results;
  }

  return [];
};

const getUserIdLabel = (user) => (
  user?.user_id
  || user?.userId
  || user?.id
  || user?.employeeId
  || user?.uuid
  || '--'
);

const getUserPropertiesCount = (user) => {
  if (Array.isArray(user?.propertyDetails)) {
    return user.propertyDetails.length;
  }

  if (Array.isArray(user?.property_details)) {
    return user.property_details.length;
  }

  if (typeof user?.propertiesCount === 'number') {
    return user.propertiesCount;
  }

  if (typeof user?.propertyCount === 'number') {
    return user.propertyCount;
  }

  if (Array.isArray(user?.properties)) {
    return user.properties.length;
  }

  if (Array.isArray(user?.property_ids)) {
    return user.property_ids.length;
  }

  if (Array.isArray(user?.propertyIds)) {
    return user.propertyIds.length;
  }

  return null;
};

const getUserPropertyDetails = (user) => {
  if (Array.isArray(user?.propertyDetails)) {
    return user.propertyDetails;
  }

  if (Array.isArray(user?.property_details)) {
    return user.property_details;
  }

  return [];
};

const validateSignupLikeForm = (formData) => {
  const newErrors = {};

  if (!formData.firstName.trim()) {
    newErrors.firstName = 'First name is required';
  }

  if (!formData.lastName.trim()) {
    newErrors.lastName = 'Last name is required';
  }

  if (!formData.email) {
    newErrors.email = 'Email is required';
  } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
    newErrors.email = 'Please enter a valid email address';
  }

  if (!formData.phone) {
    newErrors.phone = 'Phone number is required';
  } else if (!/^\+?[\d\s\-()]{10,}$/.test(formData.phone)) {
    newErrors.phone = 'Please enter a valid phone number';
  }

  if (!formData.password) {
    newErrors.password = 'Password is required';
  } else if (formData.password.length < 8) {
    newErrors.password = 'Password must be at least 8 characters';
  } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
    newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
  }

  if (!formData.confirmPassword) {
    newErrors.confirmPassword = 'Please confirm your password';
  } else if (formData.password !== formData.confirmPassword) {
    newErrors.confirmPassword = 'Passwords do not match';
  }

  return newErrors;
};

// eslint-disable-next-line max-lines-per-function
const UserManagement = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('All Roles');
  const [userTypeFilter, setUserTypeFilter] = useState('All Types');
  const [showDeleted, setShowDeleted] = useState(false);
  const [page, setPage] = useState(1);
  const [deactivatingUserKey, setDeactivatingUserKey] = useState('');
  const [deletingUserKey, setDeletingUserKey] = useState('');
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [isPropertiesDialogOpen, setIsPropertiesDialogOpen] = useState(false);
  const [propertiesDialogUser, setPropertiesDialogUser] = useState(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingUserKey, setEditingUserKey] = useState('');
  const [editingUserId, setEditingUserId] = useState(null);
  const [addForm, setAddForm] = useState(getDefaultAddForm);
  const [addFormErrors, setAddFormErrors] = useState({});
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState(getDefaultEditForm);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await userManagementAPI.getUsers(showDeleted);
        const users = getUsersFromResponse(response);
        setAllUsers(users);
      } catch (fetchError) {
        const apiMessage = fetchError?.response?.data?.message;
        setError(apiMessage || 'Unable to load users. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [showDeleted]);

  const filteredUsers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return allUsers.filter((user) => {
      const normalizedRoleForFilter = getNormalizedRoleForFilter(user);
      const status = mapStatus(user);
      const userType = String(mapUserType(user) || '')
        .trim()
        .toLowerCase();
      const name = mapName(user);
      const email = user?.email || user?.email_id || '';
      const phone = user?.phone || user?.mobile || user?.phoneNumber || '';

      const matchesRole = roleFilter === 'All Roles' || normalizedRoleForFilter === roleFilter;
      const matchesUserType = userTypeFilter === 'All Types'
        || userType === userTypeFilter.toLowerCase();
      const matchesSearch = !normalizedSearch
        || name.toLowerCase()
          .includes(normalizedSearch)
        || email.toLowerCase()
          .includes(normalizedSearch)
        || String(phone).toLowerCase()
          .includes(normalizedSearch)
        || String(status).toLowerCase()
          .includes(normalizedSearch);

      return matchesRole && matchesUserType && matchesSearch;
    });
  }, [allUsers, roleFilter, userTypeFilter, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / ROWS_PER_PAGE));
  const safePage = Math.min(page, totalPages);

  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [page, safePage]);

  const pagedUsers = useMemo(() => {
    const start = (safePage - 1) * ROWS_PER_PAGE;
    return filteredUsers.slice(start, start + ROWS_PER_PAGE);
  }, [filteredUsers, safePage]);

  const rangeStart = filteredUsers.length === 0 ? 0 : (safePage - 1) * ROWS_PER_PAGE + 1;
  const rangeEnd = Math.min(safePage * ROWS_PER_PAGE, filteredUsers.length);

  const visiblePages = useMemo(() => {
    const maxButtons = 5;
    const start = Math.max(1, safePage - 2);
    const end = Math.min(totalPages, start + maxButtons - 1);
    const adjustedStart = Math.max(1, end - maxButtons + 1);

    const pages = [];
    for (let pageNumber = adjustedStart; pageNumber <= end; pageNumber += 1) {
      pages.push(pageNumber);
    }
    return pages;
  }, [safePage, totalPages]);

  const getUserKey = (user) => {
    const joinedOn = user?.joinedOn || user?.createdAt || user?.created_at || '';
    return user?.id || user?.userId || user?.email || user?.email_id || `${mapName(user)}-${joinedOn}`;
  };

  const openEditDialog = (user) => {
    setEditingUserKey(String(getUserKey(user)));
    setEditingUserId(user?.id || user?.userId || user?.user_id || null);

    const resolvedUserType = normalizeUserTypeOption(mapUserType(user));

    setEditForm({
      firstName: user?.firstName || user?.first_name || '',
      lastName: user?.lastName || user?.last_name || '',
      email: user?.email || user?.email_id || '',
      phone: user?.phone || user?.mobile || user?.phoneNumber || '',
      password: user?.password || '',
      role: getNormalizedRoleForFilter(user),
      userType: resolvedUserType,
    });
    setIsEditDialogOpen(true);
  };

  const openViewDialog = (user) => {
    setViewingUser(user);
    setIsViewDialogOpen(true);
  };

  const openPropertiesDialog = (user) => {
    setPropertiesDialogUser(user);
    setIsPropertiesDialogOpen(true);
  };

  const openAddDialog = () => {
    setAddForm(getDefaultAddForm());
    setAddFormErrors({});
    setIsAddDialogOpen(true);
  };

  const closeAddDialog = () => {
    setIsAddDialogOpen(false);
    setAddFormErrors({});
  };

  const closeEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingUserKey('');
    setEditingUserId(null);
  };

  const closeViewDialog = () => {
    setIsViewDialogOpen(false);
    setViewingUser(null);
  };

  const closePropertiesDialog = () => {
    setIsPropertiesDialogOpen(false);
    setPropertiesDialogUser(null);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddFormChange = (field, value) => {
    setAddForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (addFormErrors[field]) {
      setAddFormErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const addUser = async () => {
    const validationErrors = validateSignupLikeForm(addForm);
    if (Object.keys(validationErrors).length > 0) {
      setAddFormErrors(validationErrors);
      return;
    }

    try {
      setError('');
      setAddFormErrors({});
      setIsAddingUser(true);

      await authAPI.register({
        firstName: addForm.firstName.trim(),
        lastName: addForm.lastName.trim(),
        email: addForm.email.trim(),
        phone: addForm.phone.trim(),
        password: addForm.password,
        role: addForm.role,
        userType: addForm.userType.toLowerCase(),
      });

      const response = await userManagementAPI.getUsers(showDeleted);
      setAllUsers(getUsersFromResponse(response));
      closeAddDialog();
    } catch (addError) {
      const apiMessage = addError?.response?.data?.message;
      setError(apiMessage || 'Registration failed. Please try again.');
    } finally {
      setIsAddingUser(false);
    }
  };

  const saveEditedUser = async () => {
    const userTypeValue = normalizeUserTypeOption(editForm.userType);
    if (!userTypeValue) {
      setError('Please select a valid user type.');
      return;
    }

    const userIdFromRow = allUsers.find((item) => String(getUserKey(item)) === String(editingUserKey));
    const resolvedUserId = editingUserId || userIdFromRow?.id || userIdFromRow?.userId || userIdFromRow?.user_id;

    if (!resolvedUserId) {
      setError('Unable to update user: missing user ID.');
      return;
    }

    const payload = {
      firstName: editForm.firstName.trim(),
      lastName: editForm.lastName.trim(),
      email: editForm.email.trim(),
      phone: editForm.phone.trim(),
      userType: userTypeValue.toLowerCase(),
      user_type: userTypeValue.toLowerCase(),
    };

    if (editForm.role === 'Admin') {
      payload.role_id = 1;
    }

    if (editForm.role === 'Admin') {
      payload.role_id = 1;
    }

    if (editForm.password && String(editForm.password).trim()) {
      payload.password = editForm.password;
    }

    try {
      setError('');
      setIsSavingEdit(true);
      await userManagementAPI.updateUser(resolvedUserId, payload);

      const response = await userManagementAPI.getUsers(showDeleted);
      setAllUsers(getUsersFromResponse(response));
      closeEditDialog();
    } catch (saveError) {
      const apiMessage = saveError?.response?.data?.message;
      setError(apiMessage || 'Failed to update user. Please try again.');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleToggleUserStatus = async (user) => {
    const userId = user?.id || user?.userId || user?.user_id;
    const userKey = String(getUserKey(user));
    const shouldActivate = isInactiveUser(user);
    const actionLabel = shouldActivate ? 'activate' : 'deactivate';

    if (!userId) {
      setError(`Unable to ${actionLabel} user: missing userId`);
      return;
    }

    const confirmed = window.confirm(`Are you sure you want to ${actionLabel} this user?`);
    if (!confirmed) {
      return;
    }

    try {
      setError('');
      setDeactivatingUserKey(userKey);
      if (shouldActivate) {
        await userManagementAPI.activateUser(userId);
      } else {
        await userManagementAPI.deactivateUser(userId);
      }

      setAllUsers((prevUsers) => prevUsers.map((item) => {
        if (String(getUserKey(item)) !== userKey) {
          return item;
        }

        return {
          ...item,
          is_active: shouldActivate ? 1 : 0,
          status: shouldActivate ? 'Active' : 'Blocked',
          isBlocked: !shouldActivate,
          isActive: shouldActivate,
        };
      }));
    } catch (toggleError) {
      const apiMessage = toggleError?.response?.data?.message;
      setError(apiMessage || `Failed to ${actionLabel} user. Please try again.`);
    } finally {
      setDeactivatingUserKey('');
    }
  };

  const handleDeleteUser = async (user) => {
    const userId = user?.id || user?.userId || user?.user_id;
    const userKey = String(getUserKey(user));

    if (!userId) {
      setError('Unable to delete user: missing userId');
      return;
    }

    const confirmed = window.confirm('Are you sure you want to delete this user?');
    if (!confirmed) {
      return;
    }

    try {
      setError('');
      setDeletingUserKey(userKey);
      await userManagementAPI.deleteUser(userId);

      setAllUsers((prevUsers) => prevUsers.filter((item) => String(getUserKey(item)) !== userKey));
    } catch (deleteError) {
      const apiMessage = deleteError?.response?.data?.message;
      setError(apiMessage || 'Failed to delete user. Please try again.');
    } finally {
      setDeletingUserKey('');
    }
  };

  return (
    <Box className="user-management-page">
      <Box className="user-management-content">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          className="page-header"
        >
          <Box className="header-content">
            <Typography variant="h5" className="page-title">
              User Management
            </Typography>
            <Typography variant="body2" className="page-subtitle">
              {filteredUsers.length} users shown
            </Typography>
          </Box>
          <Box className="header-actions">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              className="add-property user-management-add-btn"
              onClick={openAddDialog}
            >
              Add User
            </Button>
          </Box>
        </Stack>

        <section className="controls-row user-management-filters">
          <div className="search-wrap">
            <SearchIcon fontSize="small" className="search-leading-icon" />
            <input
              className="search-input"
              placeholder="Search users by name, email, phone or status"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="filters-and-toggles">
            <div className="filter-dropdowns">
              <div className="role-chip-group">
                {['All Roles', 'User', 'Admin'].map((role) => (
                  <button
                    key={role}
                    type="button"
                    className={`role-chip ${roleFilter === role ? 'is-active' : ''}`}
                    onClick={() => {
                      setRoleFilter(role);
                      setPage(1);
                    }}
                  >
                    {role}
                  </button>
                ))}
              </div>

              <select
                className="filter-dropdown"
                value={userTypeFilter}
                onChange={(event) => {
                  setUserTypeFilter(event.target.value);
                  setPage(1);
                }}
              >
                <option value="All Types">All Types</option>
                <option value="Owner">Owner</option>
                <option value="Broker">Broker</option>
                <option value="Buyer">Buyer</option>
              </select>

              <label className="deleted-toggle">
                <input
                  type="checkbox"
                  checked={showDeleted}
                  onChange={(event) => {
                    setShowDeleted(event.target.checked);
                    setPage(1);
                  }}
                />
                <span>Show deleted</span>
              </label>
            </div>
          </div>
        </section>

        {loading ? (
          <Box className="user-management-state">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            <TableContainer sx={{ mt: 2 }} className="user-management-table-scroll">
              <Table className="user-management-table">
                <TableHead>
                  <TableRow>
                    <TableCell>USER</TableCell>
                    <TableCell>CONTACT</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>USER TYPE</TableCell>
                    <TableCell>Joined On</TableCell>
                    <TableCell>PROPERTIES</TableCell>
                    <TableCell>STATUS</TableCell>
                    <TableCell align="center" className="actions-column">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedUsers.map((user) => {
                    const status = normalizeStatusLabel(mapStatus(user));
                    const isDeleted = isDeletedUser(user);
                    const joinedOn = user?.joinedOn || user?.createdAt || user?.created_at;
                    const fullName = mapName(user);
                    const role = mapRole(user);
                    const userType = mapUserType(user);
                    const userIdLabel = getUserIdLabel(user);
                    const propertiesCount = getUserPropertiesCount(user);
                    const propertyDetails = getUserPropertyDetails(user);
                    const hasPropertyDetails = propertyDetails.length > 0;
                    const key = getUserKey(user);

                    return (
                      <TableRow key={key} hover className={isDeleted ? 'user-row-deleted' : ''}>
                        <TableCell>
                          <Box className="user-cell">
                            <span className="user-avatar">{getInitials(fullName)}</span>
                            <Box>
                              <Typography className="user-name">{fullName}</Typography>
                              <Typography className="user-id-label">ID: {userIdLabel}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box className="contact-cell">
                            <Typography>
                              <MailIcon fontSize="inherit" />
                              {user?.email || user?.email_id || '--'}
                            </Typography>
                            <Typography>
                              <CallIcon fontSize="inherit" />
                              {user?.phone || user?.mobile || user?.phoneNumber || '--'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={role}
                            className={`user-chip ${getRoleChipClass(role)}`}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={userType}
                            className={`user-chip ${getUserTypeChipClass(userType)}`}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box className="joined-cell">
                            <CalendarIcon fontSize="inherit" />
                            {getDateLabel(joinedOn)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {propertiesCount === null ? (
                            <Typography className="properties-link">--</Typography>
                          ) : hasPropertyDetails ? (
                            <Button
                              size="small"
                              className="properties-link properties-link-btn"
                              onClick={() => openPropertiesDialog(user)}
                            >
                              {`${propertiesCount} props`}
                            </Button>
                          ) : (
                            <Typography className="properties-link">{`${propertiesCount} props`}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={status}
                            className={`user-chip chip-status ${getStatusChipClass(status)}`}
                            size="small"
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell align="center" className="actions-column">
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              aria-label="view-user"
                              className="action-icon-btn"
                              onClick={() => openViewDialog(user)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              aria-label="edit-user"
                              className="action-icon-btn"
                              onClick={() => openEditDialog(user)}
                              disabled={isDeleted}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={isInactiveUser(user) ? 'Activate' : 'Deactivate'}>
                            <IconButton
                              size="small"
                              aria-label={isInactiveUser(user) ? 'activate-user' : 'deactivate-user'}
                              className="action-icon-btn"
                              onClick={() => handleToggleUserStatus(user)}
                              disabled={isDeleted || deactivatingUserKey === String(getUserKey(user))}
                            >
                              {isInactiveUser(user) ? (
                                <PersonAddIcon fontSize="small" />
                              ) : (
                                <PersonOffIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              aria-label="delete-user"
                              color="error"
                              className="action-icon-btn"
                              onClick={() => handleDeleteUser(user)}
                              disabled={isDeleted || deletingUserKey === String(getUserKey(user))}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {pagedUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No users found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={2}
              sx={{ mt: 2 }}
              className="user-management-pagination-wrap"
            >
              <Typography variant="body2" color="text.secondary" className="user-management-pagination-label">
                Showing {rangeStart}-{rangeEnd} of {filteredUsers.length} users
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} className="user-management-pagination">
                <Button
                  variant="outlined"
                  size="small"
                  disabled={safePage === 1}
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  className="pagination-nav-btn"
                >
                  {'< Prev'}
                </Button>

                {visiblePages.map((pageNumber) => (
                  <Button
                    key={pageNumber}
                    variant={pageNumber === safePage ? 'contained' : 'text'}
                    size="small"
                    onClick={() => setPage(pageNumber)}
                    className={`pagination-page-btn ${pageNumber === safePage ? 'is-active' : ''}`}
                  >
                    {pageNumber}
                  </Button>
                ))}

                <Button
                  variant="outlined"
                  size="small"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  className="pagination-nav-btn"
                >
                  {'Next >'}
                </Button>
              </Stack>
            </Stack>

            <Dialog
              open={isPropertiesDialogOpen}
              onClose={closePropertiesDialog}
              fullWidth
              maxWidth="md"
            >
              <DialogTitle>Property Details</DialogTitle>
              <DialogContent dividers>
                {propertiesDialogUser ? (
                  <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {mapName(propertiesDialogUser)} - {getUserPropertiesCount(propertiesDialogUser) || 0} properties
                    </Typography>

                    {getUserPropertyDetails(propertiesDialogUser).length === 0 ? (
                      <Typography>No properties available.</Typography>
                    ) : (
                      getUserPropertyDetails(propertiesDialogUser).map((property, index) => {
                        const propertyType = property?.type || property?.propertyType || '--';
                        const propertySubType = property?.subType || property?.propertySubType || '--';
                        const area = property?.area || property?.locality || '--';
                        const taluk = property?.taluk || property?.taluka || '--';
                        const flashcardKeyword = property?.flashcardKeyword || property?.flashcard_keyword || '--';
                        const locationLabel = [area, taluk].filter((value) => value && value !== '--').join(', ') || '--';
                        const displayKeyword = String(flashcardKeyword).trim() || '--';

                        return (
                          <Box key={`${propertyType}-${propertySubType}-${index}`} className="user-property-detail-card">
                            <Typography variant="subtitle2" className="user-property-detail-title">
                              {propertyType} - {propertySubType}
                            </Typography>
                            <Typography variant="body2" className="user-property-detail-location">
                              <LocationOnOutlinedIcon fontSize="inherit" />
                              <span>{locationLabel}</span>
                            </Typography>
                            <Typography variant="body2" className="user-property-detail-keyword">
                              {displayKeyword}
                            </Typography>
                          </Box>
                        );
                      })
                    )}
                  </Stack>
                ) : null}
              </DialogContent>
              <DialogActions>
                <Button onClick={closePropertiesDialog} variant="contained">Close</Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={isViewDialogOpen}
              onClose={closeViewDialog}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>User Details</DialogTitle>
              <DialogContent dividers>
                {viewingUser ? (
                  <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Name</Typography>
                      <Typography>{mapName(viewingUser)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <Typography>{viewingUser?.email || viewingUser?.email_id || '--'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Phone</Typography>
                      <Typography>{viewingUser?.phone || viewingUser?.mobile || viewingUser?.phoneNumber || '--'}</Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">Role</Typography>
                        <Typography>{mapRole(viewingUser)}</Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">User Type</Typography>
                        <Typography>{mapUserType(viewingUser)}</Typography>
                      </Box>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Typography>{normalizeStatusLabel(mapStatus(viewingUser))}</Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">Joined On</Typography>
                        <Typography>{getDateLabel(viewingUser?.joinedOn || viewingUser?.createdAt || viewingUser?.created_at)}</Typography>
                      </Box>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">User ID</Typography>
                        <Typography>{getUserIdLabel(viewingUser)}</Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">Properties</Typography>
                        <Typography>
                          {getUserPropertiesCount(viewingUser) === null ? '--' : `${getUserPropertiesCount(viewingUser)} props`}
                        </Typography>
                      </Box>
                    </Stack>
                  </Stack>
                ) : null}
              </DialogContent>
              <DialogActions>
                <Button onClick={closeViewDialog} variant="contained">Close</Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={isAddDialogOpen}
              onClose={closeAddDialog}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>Add User</DialogTitle>
              <DialogContent dividers>
                <Stack spacing={2} sx={{ mt: 0.5 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="First Name"
                      fullWidth
                      size="small"
                      value={addForm.firstName}
                      error={Boolean(addFormErrors.firstName)}
                      helperText={addFormErrors.firstName || ' '}
                      onChange={(event) => handleAddFormChange('firstName', event.target.value)}
                    />
                    <TextField
                      label="Last Name"
                      fullWidth
                      size="small"
                      value={addForm.lastName}
                      error={Boolean(addFormErrors.lastName)}
                      helperText={addFormErrors.lastName || ' '}
                      onChange={(event) => handleAddFormChange('lastName', event.target.value)}
                    />
                  </Stack>
                  <TextField
                    label="Email"
                    fullWidth
                    size="small"
                    type="email"
                    value={addForm.email}
                    error={Boolean(addFormErrors.email)}
                    helperText={addFormErrors.email || ' '}
                    onChange={(event) => handleAddFormChange('email', event.target.value)}
                  />
                  <TextField
                    label="Phone"
                    fullWidth
                    size="small"
                    value={addForm.phone}
                    error={Boolean(addFormErrors.phone)}
                    helperText={addFormErrors.phone || ' '}
                    onChange={(event) => handleAddFormChange('phone', event.target.value)}
                  />
                  <TextField
                    label="Password"
                    fullWidth
                    size="small"
                    type="password"
                    value={addForm.password}
                    error={Boolean(addFormErrors.password)}
                    helperText={addFormErrors.password || ' '}
                    onChange={(event) => handleAddFormChange('password', event.target.value)}
                  />
                  <TextField
                    label="Confirm Password"
                    fullWidth
                    size="small"
                    type="password"
                    value={addForm.confirmPassword}
                    error={Boolean(addFormErrors.confirmPassword)}
                    helperText={addFormErrors.confirmPassword || ' '}
                    onChange={(event) => handleAddFormChange('confirmPassword', event.target.value)}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      select
                      label="Role"
                      fullWidth
                      size="small"
                      value={addForm.role}
                      onChange={(event) => handleAddFormChange('role', event.target.value)}
                    >
                      {ROLE_OPTIONS.map((roleOption) => (
                        <MenuItem key={roleOption} value={roleOption}>{roleOption}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="User Type"
                      fullWidth
                      size="small"
                      value={addForm.userType}
                      onChange={(event) => handleAddFormChange('userType', event.target.value)}
                    >
                      {USER_TYPE_OPTIONS.map((userTypeOption) => (
                        <MenuItem key={userTypeOption} value={userTypeOption}>{userTypeOption}</MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={closeAddDialog} variant="outlined" disabled={isAddingUser}>Cancel</Button>
                <Button onClick={addUser} variant="contained" disabled={isAddingUser}>
                  {isAddingUser ? 'Adding...' : 'Add User'}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={isEditDialogOpen}
              onClose={closeEditDialog}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>Edit User Profile</DialogTitle>
              <DialogContent dividers>
                <Stack spacing={2} sx={{ mt: 0.5 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="First Name"
                      fullWidth
                      size="small"
                      value={editForm.firstName}
                      onChange={(event) => handleEditFormChange('firstName', event.target.value)}
                    />
                    <TextField
                      label="Last Name"
                      fullWidth
                      size="small"
                      value={editForm.lastName}
                      onChange={(event) => handleEditFormChange('lastName', event.target.value)}
                    />
                  </Stack>
                  <TextField
                    label="Email"
                    fullWidth
                    size="small"
                    type="email"
                    value={editForm.email}
                    onChange={(event) => handleEditFormChange('email', event.target.value)}
                  />
                  <TextField
                    label="Phone"
                    fullWidth
                    size="small"
                    value={editForm.phone}
                    onChange={(event) => handleEditFormChange('phone', event.target.value)}
                  />
                  <TextField
                    label="Password"
                    fullWidth
                    size="small"
                    type="password"
                    value={editForm.password}
                    onChange={(event) => handleEditFormChange('password', event.target.value)}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      select
                      label="Role"
                      fullWidth
                      size="small"
                      value={editForm.role}
                      onChange={(event) => handleEditFormChange('role', event.target.value)}
                    >
                      {ROLE_OPTIONS.map((roleOption) => (
                        <MenuItem key={roleOption} value={roleOption}>{roleOption}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="User Type"
                      fullWidth
                      size="small"
                      value={editForm.userType}
                      onChange={(event) => handleEditFormChange('userType', event.target.value)}
                    >
                      {USER_TYPE_OPTIONS.map((userTypeOption) => (
                        <MenuItem key={userTypeOption} value={userTypeOption}>{userTypeOption}</MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={closeEditDialog} variant="outlined" disabled={isSavingEdit}>Cancel</Button>
                <Button onClick={saveEditedUser} variant="contained" disabled={isSavingEdit}>
                  {isSavingEdit ? 'Saving...' : 'Save'}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Box>
    </Box>
  );
};

export default UserManagement;
